import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { productsApi } from '@/services/products';
import { thumbnailCache } from '@/utils/thumbnailCache';
import { generateThumbnail } from '@/utils/thumbnailRenderer';

interface ThumbnailState {
  readonly url: string | null;
  readonly isGenerating: boolean;
}

export const useModelThumbnail = (
  productId: string,
  fileKey: string | null,
): ThumbnailState => {
  const [state, setState] = useState<ThumbnailState>({
    url: null,
    isGenerating: false,
  });
  const objectUrlRef = useRef<string | null>(null);

  // Fetch presigned download URL (only when we need to generate)
  const { data: modelUrl } = useQuery({
    queryKey: ['product-file-url', productId],
    queryFn: () => productsApi.getFileDownloadUrl(productId),
    enabled: !!fileKey && state.url === null && !state.isGenerating,
    staleTime: 10 * 60 * 1000,
  });

  // Check cache on mount / when fileKey changes
  useEffect(() => {
    if (!fileKey) return;

    let cancelled = false;

    const checkCache = async () => {
      const cached = await thumbnailCache.get(productId, fileKey);
      if (cancelled) return;

      if (cached) {
        const url = URL.createObjectURL(cached);
        objectUrlRef.current = url;
        setState({ url, isGenerating: false });
      } else {
        // Signal that we need to generate
        setState({ url: null, isGenerating: false });
      }
    };

    checkCache();

    return () => {
      cancelled = true;
    };
  }, [productId, fileKey]);

  // Generate thumbnail when we have the model URL and no cached version
  useEffect(() => {
    if (!fileKey || !modelUrl || state.url !== null || state.isGenerating) return;

    let cancelled = false;

    const generate = async () => {
      setState((prev) => ({ ...prev, isGenerating: true }));

      try {
        const blob = await generateThumbnail(modelUrl);
        if (cancelled) return;

        await thumbnailCache.put(productId, fileKey, blob);

        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setState({ url, isGenerating: false });
      } catch {
        if (!cancelled) {
          setState({ url: null, isGenerating: false });
        }
      }
    };

    generate();

    return () => {
      cancelled = true;
    };
  }, [productId, fileKey, modelUrl, state.url, state.isGenerating]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  return state;
};
